import { motion } from "framer-motion";
import { MapPin, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { SITE } from "../../data/site";
import { CITIES } from "../../data/cities";

const stateCities = (code) => CITIES.filter((c) => c.stateCode === code);

const Column = ({ state, cities, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay }}
    className="border-t border-[#E5DFD5] pt-6"
  >
    <div className="flex items-center justify-between mb-6">
      <h3 className="font-display text-[#1C1B1A] text-3xl md:text-4xl tracking-tight">
        {state}
      </h3>
      <span className="text-xs text-[#5C5A55] font-mono">
        {cities.length} cities
      </span>
    </div>
    <ul className="grid grid-cols-2 gap-x-6 gap-y-1">
      {cities.map((c) => (
        <li key={c.slug}>
          <Link
            to={`/service-areas/${c.slug}`}
            data-testid={`area-link-${c.slug}`}
            className="group flex items-center justify-between gap-2 text-[#1C1B1A] text-base md:text-lg border-b border-[#E5DFD5]/60 py-3 hover:text-[#C05746] transition-colors"
          >
            <span className="flex items-center gap-2">
              <MapPin
                size={14}
                className="text-[#C05746] opacity-60 group-hover:opacity-100 transition-opacity"
              />
              {c.city}
            </span>
            <ArrowUpRight
              size={14}
              className="opacity-40 group-hover:opacity-100 group-hover:-translate-y-0.5 transition-all"
            />
          </Link>
        </li>
      ))}
    </ul>
  </motion.div>
);

export const ServiceAreas = () => (
  <section
    id="areas"
    data-testid="areas-section"
    className="bg-[#F9F7F4] py-24 md:py-32 border-t border-[#E5DFD5]"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <div className="grid grid-cols-12 gap-6 mb-14 items-end">
        <div className="col-span-12 lg:col-span-8">
          <div className="text-[#C05746] text-xs md:text-sm font-semibold tracking-[0.24em] uppercase mb-4">
            Where we work
          </div>
          <h2 className="font-display text-[#1C1B1A] text-4xl md:text-5xl lg:text-[4.5rem] leading-[1.02] tracking-tight">
            Southern PA,
            <br />
            <span className="font-serif-accent italic text-[#C05746]">most</span> of Maryland.
          </h2>
        </div>
        <p className="col-span-12 lg:col-span-4 text-[#5C5A55] text-base md:text-lg leading-relaxed">
          If you're near any of these towns, you're in our service area.
          Not sure? Call us — we probably cover you anyway.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
        <Column state="Pennsylvania" cities={stateCities("PA")} delay={0} />
        <Column state="Maryland" cities={stateCities("MD")} delay={0.15} />
      </div>

      <div className="mt-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-t border-[#E5DFD5] pt-8">
        <p className="text-[#5C5A55] text-sm md:text-base">
          Based in Glen Rock, PA · Serving neighbors across two states
        </p>
        <a
          href={SITE.phoneHref}
          data-testid="areas-call-button"
          className="text-[#1C1B1A] font-semibold uppercase tracking-[0.14em] text-sm link-underline"
        >
          Check your town → {SITE.phoneDisplay}
        </a>
      </div>
    </div>
  </section>
);
